import { formatArtifactSummary, resolveArtifactsById, type LeadGenArtifact } from "./artifacts";
import type { OutboundTouchKind } from "./types";
import defaultTemplates from "./templates.json";

export type OutreachTemplateKind = "gem" | "customgpt";

export interface OutreachTemplateTiming {
    kind: OutboundTouchKind;
    offset_days: number;
}

/** Saved template (gem / custom GPT): cadence + standing instructions. */
export interface OutreachTemplate {
    id: string;
    name: string;
    kind: OutreachTemplateKind;
    timing: OutreachTemplateTiming[];
    instructions: string;
    /** Artefacts from the shared library to include in AI prompts. */
    artifact_ids: string[];
    /** When true, used as the default pick for new leads. At most one template should be default. */
    is_default?: boolean;
}

export function getTemplateArtifacts(
    template: Pick<OutreachTemplate, "artifact_ids"> | null | undefined,
    library: LeadGenArtifact[] = []
): LeadGenArtifact[] {
    return resolveArtifactsById(template?.artifact_ids, library);
}

export function formatTemplateArtifactSummary(
    template: Pick<OutreachTemplate, "artifact_ids"> | null | undefined,
    library: LeadGenArtifact[] = []
): string {
    return formatArtifactSummary(getTemplateArtifacts(template, library));
}

export const DEFAULT_OUTREACH_TEMPLATES = defaultTemplates as OutreachTemplate[];

export function getDefaultOutreachTemplateId(templates: OutreachTemplate[]): string {
    return templates.find((template) => template.is_default)?.id ?? templates[0]?.id ?? "";
}

export function getOutreachTemplate(templates: OutreachTemplate[], templateId: string): OutreachTemplate | undefined {
    return templates.find((template) => template.id === templateId) ?? templates[0];
}

export function formatTemplateTimingSummary(timing: OutreachTemplateTiming[]): string {
    return timing
        .map((step) => {
            const label = step.kind === "connect" ? "Connect" : step.kind === "email" ? "Email" : "LinkedIn";
            if (step.offset_days === 0) return `${label} (day 0)`;
            return `${label} (+${step.offset_days}d)`;
        })
        .join(" · ");
}

export type OutreachTimingUnit = "day" | "week" | "month";

/** Editable form row for touch cadence (mirrors schedule builder interval + unit). */
export interface OutreachTimingStep {
    kind: OutboundTouchKind;
    offset: number;
    time_unit: OutreachTimingUnit;
}

export function templateTimingToFormSteps(timing: OutreachTemplateTiming[]): OutreachTimingStep[] {
    return timing.map((step) => ({
        kind: step.kind,
        offset: step.offset_days,
        time_unit: "day" as const,
    }));
}

export function formStepsToTemplateTiming(steps: OutreachTimingStep[]): OutreachTemplateTiming[] {
    return steps.map((step) => {
        const offset = Number.isFinite(step.offset) ? Math.max(0, step.offset) : 0;
        const offset_days = step.time_unit === "week" ? offset * 7 : step.time_unit === "month" ? offset * 30 : offset;
        return { kind: step.kind, offset_days };
    });
}
